import { CapacityGranularity, WorkloadPeriod } from "@planzone/dto";

type MonthGroup = { label: string; span: number };

function getISOWeekNumber(date: Date): number {
  const target = new Date(date.getTime());
  target.setHours(0, 0, 0, 0);
  target.setDate(target.getDate() + 3 - ((target.getDay() + 6) % 7));
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const diff = target.getTime() - firstThursday.getTime();

  return 1 + Math.round(diff / (7 * 24 * 60 * 60 * 1000));
}

function toISODate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);

  result.setDate(result.getDate() + days);

  return result;
}

function startOfISOWeek(date: Date): Date {
  const dayIndex = (date.getDay() + 6) % 7;

  return addDays(date, -dayIndex);
}

function computeExpectedPeriods(
  from: string,
  to: string,
  granularity: CapacityGranularity,
): WorkloadPeriod[] {
  const rangeStart = new Date(`${from}T00:00:00`);
  const rangeEnd = new Date(`${to}T00:00:00`);
  const periods: WorkloadPeriod[] = [];

  if (granularity === "MONTH") {
    const cursor = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1);

    while (cursor <= rangeEnd) {
      const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
      const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);

      periods.push({
        from: toISODate(monthStart < rangeStart ? rangeStart : monthStart),
        to: toISODate(monthEnd > rangeEnd ? rangeEnd : monthEnd),
        capacity: 0,
        load: 0,
      });
      cursor.setMonth(cursor.getMonth() + 1);
    }

    return periods;
  }

  let weekStart = startOfISOWeek(rangeStart);

  while (weekStart <= rangeEnd) {
    const weekEnd = addDays(weekStart, 6);

    periods.push({
      from: toISODate(weekStart < rangeStart ? rangeStart : weekStart),
      to: toISODate(weekEnd > rangeEnd ? rangeEnd : weekEnd),
      capacity: 0,
      load: 0,
    });
    weekStart = addDays(weekStart, 7);
  }

  return periods;
}

function computeNaturalPeriodBounds(
  referenceDate: string,
  granularity: CapacityGranularity,
): { from: string; to: string } {
  const reference = new Date(`${referenceDate}T00:00:00`);

  if (granularity === "MONTH") {
    const monthStart = new Date(
      reference.getFullYear(),
      reference.getMonth(),
      1,
    );
    const monthEnd = new Date(
      reference.getFullYear(),
      reference.getMonth() + 1,
      0,
    );

    return { from: toISODate(monthStart), to: toISODate(monthEnd) };
  }

  const weekStart = startOfISOWeek(reference);
  const weekEnd = addDays(weekStart, 6);

  return { from: toISODate(weekStart), to: toISODate(weekEnd) };
}

function formatPeriodLabel(
  period: WorkloadPeriod,
  granularity: CapacityGranularity,
  locale: string,
): string {
  const from = new Date(period.from);

  return granularity === "MONTH"
    ? from.toLocaleDateString(locale, { month: "short" })
    : `S${getISOWeekNumber(from)}`;
}

function groupPeriodsByMonth(
  periods: WorkloadPeriod[],
  locale: string,
): MonthGroup[] {
  return periods.reduce<MonthGroup[]>((groups, period) => {
    const label = new Date(period.from).toLocaleDateString(locale, {
      month: "long",
      year: "numeric",
    });
    const last = groups[groups.length - 1];

    if (last && last.label === label) {
      return [...groups.slice(0, -1), { ...last, span: last.span + 1 }];
    }

    return [...groups, { label, span: 1 }];
  }, []);
}

export type { MonthGroup };

export {
  computeExpectedPeriods,
  computeNaturalPeriodBounds,
  formatPeriodLabel,
  groupPeriodsByMonth,
};
